import React, { Suspense } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import "./App.css";
import "./index.css";
import Navbar from "@layout/navigation/NavBar/Navbar";
import Footer from "@layout/footer/Footer";
import Home from "./views/home/Home";
import Error from "@views/Error";
import Login from "@views/authentication/Login";
import AuthVerify from "./components/common/AuthVerify/AuthVerify";
import ProtectedRoute from "./components/routing/ProtectedRoute";

import ROLES from "./utils/constants/roles.constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LinearProgress } from "@mui/material";
import UserManual from "./views/userManual/UserManual";
import AnonHome from "./views/home/AnonHome";
import Auth from "./services/Auth/Auth";

const Admin = React.lazy(() => import("@admin-views/data/admin/Admin"));
const AdminForm = React.lazy(() => import("@admin-views/data/admin/AdminForm"));
const StafSekre = React.lazy(() =>
  import("@admin-views/data/staf-sekre/StafSekre")
);
const Manajer = React.lazy(() => import("@admin-views/data/manajer/Manajer"));
const ManajerForm = React.lazy(() =>
  import("@admin-views/data/manajer/ManajerForm")
);
const Dosen = React.lazy(() => import("@admin-views/data/dosen/Dosen"));
const Mahasiswa = React.lazy(() =>
  import("@admin-views/data/mahasiswa/Mahasiswa")
);
const MkSpesial = React.lazy(() =>
  import("@admin-views/sidang/mk-spesial/MkSpesial")
);
const Ruangan = React.lazy(() => import("@admin-views/sidang/ruangan/Ruangan"));
const JadwalRuangan = React.lazy(() =>
  import("@admin-views/sidang/jadwal-ruangan/JadwalRuangan")
);
const Deadline = React.lazy(() =>
  import("@admin-views/sidang/deadline/Deadline")
);
const Laporan = React.lazy(() => import("@admin-views/sidang/laporan/Laporan"));
const Log = React.lazy(() => import("@admin-views/sidang/log/Log"));
const JadwalPribadi = React.lazy(() =>
  import("@dosen-views/jadwal-pribadi/JadwalPribadi")
);
const CreateDosen = React.lazy(() =>
  import("@admin-views/data/dosen/CreateDosen")
);
const CreateMahasiswa = React.lazy(() =>
  import("./views/admin/data/mahasiswa/CreateMahasiswa")
);
const UpdateDosen = React.lazy(() =>
  import("./views/admin/data/dosen/UpdateDosen")
);
const Term = React.lazy(() => import("./views/admin/sidang/term/Term"));
const CreateTerm = React.lazy(() =>
  import("./views/admin/sidang/term/CreateTerm")
);
const CreateMKSpesial = React.lazy(() =>
  import("./views/admin/sidang/mk-spesial/CreateMKSpesial")
);
const Profile = React.lazy(() => import("./views/profile/Profile"));
const JadwalPribadiDosen = React.lazy(() =>
  import("./views/admin/data/dosen/JadwalPribadiDosen")
);
const SidangMahasiswa = React.lazy(() =>
  import("./views/mahasiswa/SidangMahasiswa")
);
const JadwalSidangAdmin = React.lazy(() =>
  import("./views/admin/sidang/jadwal-sidang/JadwalSidangAdmin")
);
const JadwalSidangForm = React.lazy(() =>
  import("./views/admin/sidang/jadwal-sidang/JadwalSidangForm")
);
const PenilaianSidang = React.lazy(() =>
  import("./views/dosen/sidang/penilaian/PenilaianSidang")
);
const BerkasSidangDetail = React.lazy(() =>
  import("./views/dosen/sidang/berkas/BerkasSidangDetail")
);
const EvaluasiSidang = React.lazy(() =>
  import("./views/dosen/sidang/evaluasi/EvaluasiSidang")
);
// const EvaluasiList = React.lazy(() =>
//   import("./views/dosen/sidang/evaluasi/evaluasi-list/EvaluasiList")
// );

function App() {
  const navigate = useNavigate();
  const adminOnlyPageRoles = [ROLES.DEVELOPER, ROLES.ADMIN];
  const adminAndManagersOnlyPageRoles = [
    ROLES.DEVELOPER,
    ROLES.ADMIN,
    ROLES.MANAJER,
  ];
  const dosenAndMahasiswaOnlyPageRoles = [
    ROLES.DEVELOPER,
    ROLES.DOSEN,
    ROLES.MAHASISWA,
  ];
  const sisidangManagers = [
    ROLES.DEVELOPER,
    ROLES.ADMIN,
    ROLES.MANAJER,
    ROLES.STAFF,
  ];
  const dosenOnlyPageRoles = [ROLES.DEVELOPER, ROLES.DOSEN];
  const mahasiswaOnlyPageRoles = [ROLES.DEVELOPER, ROLES.MAHASISWA];
  const isLoggedInRoles = [
    ROLES.DEVELOPER,
    ROLES.ADMIN,
    ROLES.MANAJER,
    ROLES.STAFF,
    ROLES.DOSEN,
    ROLES.MAHASISWA,
  ];
  const wrapWithProtectedRoute = (element, reqRoles) => {
    return <ProtectedRoute reqRole={reqRoles}>{element}</ProtectedRoute>;
  };

  //Kalau ada maintenace sistem, ganti jadi true
  const maintenance = false;
  return (
    <>
      {maintenance && (
        <div className="flex items-center justify-center text-center text-3xl font-bold">
          <div>
            <p>Maaf!</p>
            <p>Lagi ada perbaikan.</p>
            <p>Tolong cek kembali beberapa waktu kemudian ya!</p>
          </div>
        </div>
      )}
      {!maintenance && (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div className="max-w-full">
            <div className="flex flex-col items-center h-screen max-w-full">
              <Navbar />
              <div className="flex flex-col mt-2 pt-16 items-center content-center h-screen w-full">
                <div className="px-4 pt-12 md:pt-6 xl:pt-0 grow max-w-7xl w-full">
                  <Routes>
                    <Route
                      path="/petunjuk-penggunaan"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <UserManual />
                        </Suspense>,
                        isLoggedInRoles
                      )}
                    />
                    <Route
                      path="/jadwal-sidang"
                      element={<AnonHome isDosen={true}/>}
                    />
                    <Route path="/" element={<Home />} />
                    <Route
                      path="/masuk"
                      element={<Login navigate={navigate} />}
                    />
                    <Route
                      path="/login"
                      element={<Login navigate={navigate} />}
                    />
                    <Route
                      path="/profil"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Profile />
                        </Suspense>,
                        dosenAndMahasiswaOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/data/admin"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Admin />
                        </Suspense>,
                        adminOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/data/admin/create"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <AdminForm />
                        </Suspense>,
                        adminOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/data/staf"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <StafSekre />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/data/manajer"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Manajer />
                        </Suspense>,
                        adminAndManagersOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/data/manajer/create"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <ManajerForm />
                        </Suspense>,
                        adminAndManagersOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/data/dosen"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Dosen navigate={navigate} />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/data/dosen/create"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <CreateDosen navigate={navigate} />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/data/dosen/update/:id"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <UpdateDosen navigate={navigate} />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/data/dosen/jadwal-pribadi"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <JadwalPribadiDosen />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/data/mahasiswa"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Mahasiswa />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/data/mahasiswa/create"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <CreateMahasiswa />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/mk-spesial"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <MkSpesial />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/mk-spesial/form"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <CreateMKSpesial />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/deadline"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Deadline />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/ruangan"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Ruangan />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/jadwal-sidang"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <JadwalSidangAdmin />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/jadwal-sidang/form"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <JadwalSidangForm />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/jadwal-ruangan"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <JadwalRuangan />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/semester"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Term />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/semester/create"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <CreateTerm />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/laporan"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Laporan />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/log"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <Log />
                        </Suspense>,
                        sisidangManagers
                      )}
                    />
                    <Route
                      path="/sidang/nilai"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <PenilaianSidang />
                        </Suspense>,
                        dosenOnlyPageRoles
                      )}
                    />
                    {/* <Route
                  path="/sidang/evaluasi-list"
                  element={wrapWithProtectedRoute(
                    <Suspense fallback={<LinearProgress />}>
                      <EvaluasiList />
                    </Suspense>,
                    dosenOnlyPageRoles
                  )}
                /> */}
                    <Route
                      path="/sidang/berkas/:mkid"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <BerkasSidangDetail />
                        </Suspense>,
                        [...dosenOnlyPageRoles, ...sisidangManagers]
                      )}
                    />
                    <Route
                      path="/sidang/evaluasi"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <EvaluasiSidang />
                        </Suspense>,
                        dosenOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/dosen/jadwal-pribadi"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <JadwalPribadi />
                        </Suspense>,
                        dosenOnlyPageRoles
                      )}
                    />
                    <Route
                      path="/mahasiswa/sidang"
                      element={wrapWithProtectedRoute(
                        <Suspense fallback={<LinearProgress />}>
                          <SidangMahasiswa />
                        </Suspense>,
                        mahasiswaOnlyPageRoles
                      )}
                    />
                    <Route path="/*" element={<Error />} />
                  </Routes>
                </div>
                <AuthVerify />
                <Footer />
              </div>
            </div>
          </div>
        </LocalizationProvider>
      )}
    </>
  );
}

export default App;
